import { Injectable, NgZone } from '@angular/core';
import {
  collection,
  doc,
  getDoc,
  getFirestore,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { Observable } from 'rxjs';

export interface SiteContentSection {
  id?: string;
  isActive?: boolean;
  updatedAt?: unknown;
}

export interface HeroContent extends SiteContentSection {
  eyebrow: string;
  headline: string;
  subHeadline: string;
  description: string;
  primaryButtonText: string;
  secondaryButtonText: string;
  heroImageUrl: string;
}

export interface JourneyContent extends SiteContentSection {
  eyebrow: string;
  title: string;
  description: string;
  buttonText: string;
  steps: string[];
}

export interface ProductsPageContent extends SiteContentSection {
  eyebrow: string;
  headline: string;
  description: string;
}

export interface WhyChooseUsContent extends SiteContentSection {
  eyebrow: string;
  title: string;
  description: string;
}

export interface ImpactStatContent {
  value: string;
  label: string;
}

export interface ImpactStatsContent extends SiteContentSection {
  title: string;
  stats: ImpactStatContent[];
}

export interface FooterContent extends SiteContentSection {
  companyDescription: string;
  phone: string;
  email: string;
  address: string;
  facebook: string;
  instagram: string;
  linkedin: string;
  youtube: string;
}

export interface SettingsContent extends SiteContentSection {
  siteName: string;
  adminEmailDisplay: string;
  defaultProductImageUrl: string;
  maintenanceMode: boolean;
}

export const DEFAULT_SITE_CONTENT = {
  hero: {
    eyebrow: '100% Organic • Sustainable • Chemical Free',
    headline: 'The Organic Evolution',
    subHeadline: 'Replacing Chemicals with Sustainable, Reliable Mushroom Cultivation.',
    description:
      'Ditch the Chemicals at SR ORGANICS. The future of mushroom farming is sustainable, reliable and 100% organic.',
    primaryButtonText: 'Explore Our Products',
    secondaryButtonText: 'Request a Quote',
    heroImageUrl: 'assets/sr-organic/mushroom-hero.png',
    isActive: true,
  } satisfies HeroContent,
  journey: {
    eyebrow: 'THE SR ORGANICS JOURNEY',
    title: 'From Nature To Nourishment',
    description:
      'Quality over quantity, always. By transforming traditional mushroom cultivation into a sustainable, reliable and 100% organic process, we turn fungi into meaningful eco-friendly solutions that are better for people and the planet.',
    buttonText: 'Discover Our Process',
    steps: [
      'Sourcing from Local Forests',
      'Autoclave Sterilization at 15 PSI',
      'Cultivation',
      'Ready-to-fruit Grow Kits',
      'Harvest & Same-day Delivery',
      'Nutrient-rich Eco-friendly Fertilizers',
    ],
    isActive: true,
  } satisfies JourneyContent,
  productsPage: {
    eyebrow: 'OUR PRODUCTS',
    headline: 'Sustainable, Reliable Organics for Healthy Lifestyle.',
    description:
      'Explore active SR ORGANICS product streams and their child products for food, wellness, farming, industrial and research needs.',
    isActive: true,
  } satisfies ProductsPageContent,
  whyChooseUs: {
    eyebrow: 'WHY CHOOSE SR ORGANICS?',
    title: 'Better Quality. Better Tomorrow.',
    description: 'We are committed to delivering products that are pure, safe, consistent and better for the planet.',
    isActive: true,
  } satisfies WhyChooseUsContent,
  impactStats: {
    title: 'Creating Impact. Sustainably.',
    stats: [
      { value: '12,500+', label: 'Kg Waste Recycled Every Month' },
      { value: '8,750+', label: 'Trees Equivalent Saved' },
      { value: '6,200+', label: 'Tons CO2 Emissions Reduced' },
      { value: '100%', label: 'Renewable Energy Driven' },
    ],
    isActive: true,
  } satisfies ImpactStatsContent,
  footer: {
    companyDescription:
      'Transforming mushroom-based organic solutions for a healthier planet and a better future.',
    phone: '+91 12345 67890',
    email: 'info@srorganic.com',
    address: 'A-1, Organic Park, Green City, India - 123456',
    facebook: '',
    instagram: '',
    linkedin: '',
    youtube: '',
    isActive: true,
  } satisfies FooterContent,
  settings: {
    siteName: 'S.R.ORGANIC',
    adminEmailDisplay: 'admin@srorganic.com',
    defaultProductImageUrl: 'assets/sr-organic/mushroom-hero.png',
    maintenanceMode: false,
    isActive: true,
  } satisfies SettingsContent,
};

const SITE_CONTENT_COLLECTION = 'siteContent';

@Injectable({ providedIn: 'root' })
export class SiteContentService {
  constructor(private readonly ngZone: NgZone) {}

  getSection<T>(sectionId: string): Observable<T | null> {
    const sectionRef = doc(getFirestore(), SITE_CONTENT_COLLECTION, sectionId);

    return new Observable<T | null>((subscriber) => {
      const unsubscribe = onSnapshot(
        sectionRef,
        (snapshot) => {
          this.ngZone.run(() => {
            subscriber.next(snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as T) : null);
          });
        },
        () => {
          this.ngZone.run(() => subscriber.next(null));
        },
      );

      return unsubscribe;
    });
  }

  getAllSections(): Observable<any[]> {
    const sectionsRef = collection(getFirestore(), SITE_CONTENT_COLLECTION);

    return new Observable<any[]>((subscriber) => {
      const unsubscribe = onSnapshot(
        sectionsRef,
        (snapshot) => {
          this.ngZone.run(() => {
            subscriber.next(snapshot.docs.map((sectionDoc) => ({ id: sectionDoc.id, ...sectionDoc.data() })));
          });
        },
        () => {
          this.ngZone.run(() => subscriber.next([]));
        },
      );

      return unsubscribe;
    });
  }

  async updateSection(sectionId: string, data: any): Promise<void> {
    const sectionRef = doc(getFirestore(), SITE_CONTENT_COLLECTION, sectionId);

    await setDoc(
      sectionRef,
      {
        ...data,
        isActive: data?.isActive ?? true,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  }

  async seedDefaultContent(): Promise<void> {
    await Promise.all(
      Object.entries(DEFAULT_SITE_CONTENT).map(async ([sectionId, content]) => {
        const sectionRef = doc(getFirestore(), SITE_CONTENT_COLLECTION, sectionId);
        const snapshot = await getDoc(sectionRef);

        if (!snapshot.exists()) {
          await setDoc(sectionRef, {
            ...content,
            updatedAt: serverTimestamp(),
          });
        }
      }),
    );
  }
}
